import React, { useCallback, useEffect, useState } from 'react';
import { BookMarked, Copy, ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { parseBibtexForDisplay, type BibDisplay } from '@/utils/bibtex-display';

interface Props {
  projectId: string;
  /** Bumped by the parent when a tool run may have added a citation. */
  refreshTick?: number;
  citedKeys?: string[];
}

/**
 * Compact per-project bibliography panel for Writer.
 *
 * Reads from `writer_bib_entries` — the single source of truth used by the
 * assembler, validator, materialize drain AND this UI. Rich display fields
 * (title / authors / year / venue / doi / url) are parsed from the stored
 * BibTeX so we never diverge from what the compiler actually sees.
 */
const WriterBibliographyPanel: React.FC<Props> = ({
  projectId,
  refreshTick = 0,
  citedKeys = [],
}) => {
  const [entries, setEntries] = useState<BibDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from('writer_bib_entries')
      .select('citekey, bibtex, created_at')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(200);
    const rows = ((data ?? []) as Array<{ citekey: string; bibtex: string }>).map((r) =>
      parseBibtexForDisplay(r.citekey, r.bibtex ?? ''),
    );
    setEntries(rows);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load, refreshTick]);

  const copyKey = useCallback((key: string) => {
    void navigator.clipboard.writeText(`\\cite{${key}}`);
    toast.success(`Copied \\cite{${key}}`);
  }, []);

  const remove = useCallback(
    async (citekey: string) => {
      const { error } = await (supabase as any)
        .from('writer_bib_entries')
        .delete()
        .eq('project_id', projectId)
        .eq('citekey', citekey);
      if (error) {
        toast.error('Failed to remove citation');
        return;
      }
      setEntries((prev) => prev.filter((e) => e.citekey !== citekey));
    },
    [projectId],
  );

  const count = entries.length;
  const cited = new Set(citedKeys);
  const uncitedCount = entries.filter((entry) => !cited.has(entry.citekey)).length;

  return (
    <div className="border-b border-border/60 bg-background/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2">
          <BookMarked className="h-3.5 w-3.5" />
          Bibliography
          <span className="text-muted-foreground/70 normal-case tracking-normal">
            {loading ? '…' : `${count} reference${count === 1 ? '' : 's'}`}
          </span>
          {!loading && uncitedCount > 0 ? (
            <span className="normal-case tracking-normal text-warning-foreground">
              · {uncitedCount} not cited
            </span>
          ) : null}
        </span>
        <span className="text-[10px]">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto px-3 pb-3">
          {loading ? (
            <div className="py-6 flex justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : count === 0 ? (
            <p className="text-xs text-muted-foreground py-3 px-1">
              No citations yet. Ask the assistant to resolve a paper and add it.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {entries.map((e) => (
                <li
                  key={e.citekey}
                  className="group text-xs bg-muted/30 hover:bg-muted/60 transition rounded px-2 py-1.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[11px] text-primary truncate">
                        {e.citekey}
                      </div>
                      {!cited.has(e.citekey) ? (
                        <div className="mt-0.5 text-[10px] font-medium text-warning-foreground">
                          Not cited
                        </div>
                      ) : null}
                      <div className="truncate text-foreground">
                        {e.title || <span className="text-muted-foreground italic">Untitled entry</span>}
                      </div>
                      <div className="text-muted-foreground truncate">
                        {(e.authors ?? []).slice(0, 3).join(', ')}
                        {e.year ? ` · ${e.year}` : ''}
                        {e.venue ? ` · ${e.venue}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button
                        type="button"
                        onClick={() => copyKey(e.citekey)}
                        title="Copy \cite{} command"
                        className="p-1 rounded hover:bg-background"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      {e.url && (
                        <a
                          href={e.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open source"
                          className="p-1 rounded hover:bg-background"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(e.citekey)}
                        title="Remove citation"
                        className="p-1 rounded hover:bg-background text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default WriterBibliographyPanel;
